/**
 * KK-1447: Reusable API timeout utility with AbortController
 * Provides race condition timeout for API calls with proper resource cleanup
 */

export interface ApiTimeoutOptions {
  timeoutMs: number;
  timeoutErrorMessage?: string;
  signal?: AbortSignal; // Allow external abort signals
}

export interface ApiTimeoutResult<T> {
  data: T | null;
  isTimeout: boolean;
  error?: Error;
}

// Constants
const DEFAULT_TIMEOUT_MS = 500;
const DEFAULT_ERROR_MESSAGE = 'API_REQUEST_TIMEOUT';

/**
 * Create timeout promise that rejects after specified time
 */
function createTimeoutPromise<T>(timeoutMs: number, errorMessage: string): Promise<T> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(errorMessage)), timeoutMs);
  });
}

/**
 * Combine multiple AbortSignals into one
 */
function combineSignals(signals: AbortSignal[]): AbortSignal {
  const controller = new AbortController();

  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort();
      break;
    }
    signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  return controller.signal;
}

/**
 * Execute API call with race condition timeout
 * with proper AbortController resource management
 */
export async function withApiTimeout<T>(
  apiCall: (signal: AbortSignal) => Promise<T>,
  options: ApiTimeoutOptions = { timeoutMs: DEFAULT_TIMEOUT_MS },
): Promise<ApiTimeoutResult<T>> {
  const { timeoutMs, timeoutErrorMessage = DEFAULT_ERROR_MESSAGE, signal } = options;

  // Create abort controller for timeout management
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // Combine external signal with timeout signal if provided
  const combinedSignal = signal ? combineSignals([signal, controller.signal]) : controller.signal;

  try {
    // Race between API call and timeout
    const data = await Promise.race([
      apiCall(combinedSignal),
      createTimeoutPromise(timeoutMs, timeoutErrorMessage),
    ]);

    clearTimeout(timeoutId);
    return { data, isTimeout: false };
  } catch (error) {
    clearTimeout(timeoutId);

    const isTimeoutError =
      error instanceof Error &&
      (error.name === 'AbortError' || error.message === timeoutErrorMessage);

    return {
      data: null,
      isTimeout: isTimeoutError,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}
